/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { View, Text, StyleSheet, Platform, Dimensions, TouchableOpacity } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHFilter, { DropDownOption } from "../../../components/LHFilter";
import SiteEvalDataTable from "../SiteEvalDataTable";
import FontFamily from "../../../constants/fonts";
import ActiveItemsTable from "./ActiveItemsTable";
import MobileFilterOverlay from "../../../components/MobileFilterOverlay";
import SiteEvalCardView from "./SiteEvalCardView";
import { useTheme } from "../../../theme";

interface SiteEvalMetricsProps {
  filterOptions: any[];
  onFilterChange: (
    key: string,
    value: DropDownOption | DropDownOption[] | null
  ) => void;
  isMobile: boolean;
  isLoading: boolean;
  tableData: any[];
  tableColumns: any[];
  onRowClick: (rowData: any, colIdx?: number) => void;
  styles: any;
  applyFilters?: () => Promise<void>;
  resetFilters?: () => Promise<void>;
  activeItemsData?: any[]; // For determining action plan state
  onToggleChange?: (id: number, value: boolean) => void; // For handling toggle changes
  showMobileFilterButton?: boolean; // For mobile iOS - show filter button instead of inline filters
  onMobileFilterPress?: () => void; // Handler for mobile filter button press
  currentFilters?: any; // Current applied filters for mobile overlay
  onMobileFilterProceed?: (selectedFilters: Partial<Record<string, any>>) => void; // Handler for mobile filter apply
  onGoToActionPlan?: () => void; // Handler for navigating to Action Plan module
}

// Helper function to format period/week display
const formatPeriodWeek = (item: any): string => {
  const period = item.Period || "";
  const week = item.Week || "";

  if (period && week) {
    return `P${period}W${week}`;
  } else if (period) {
    return `P${period}`;
  } else if (week) {
    return `W${week}`;
  }
  return "-";
};

const SiteEvalMetrics: React.FC<SiteEvalMetricsProps> = ({
  filterOptions,
  onFilterChange,
  isMobile,
  isLoading,
  tableData,
  tableColumns,
  onRowClick,
  styles,
  applyFilters,
  resetFilters,
  activeItemsData = [],
  onToggleChange,
  showMobileFilterButton = false,
  onMobileFilterPress,
  currentFilters = {},
  onMobileFilterProceed,
  onGoToActionPlan,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // State for mobile filter overlay
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = React.useState(false);

  const handleMobileFilterPress = () => {
    setMobileFilterOverlayVisible(true);
    onMobileFilterPress?.();
  };

  const handleMobileFilterCancel = () => {
    setMobileFilterOverlayVisible(false);
  };

  const handleMobileFilterProceed = (selectedFilters: Partial<Record<string, any>>) => {
    // Apply each filter through the parent handler
    Object.entries(selectedFilters).forEach(([key, value]) => {
      onFilterChange(key, value);
    });
    setMobileFilterOverlayVisible(false);
    // Call parent proceed handler if provided
    onMobileFilterProceed?.(selectedFilters);
  };
  // Combine period and week columns if both exist
  const customizedColumns = React.useMemo(() => {
    console.log("tablecolumns", tableColumns);
    const hasPeriod = tableColumns.some(
      (col: any) => col.columnKey === "Period"
    );
    const hasWeek = tableColumns.some((col: any) => col.columnKey === "Week");
    const hasCombined = tableColumns.some(
      (col: any) => col.columnKey === "periodWeek"
    );
    const shouldCombine = hasPeriod && hasWeek && !hasCombined;
    let combinedCreated = false;

    return tableColumns
      .map((column: any) => {
        if (column.columnKey === "periodWeek") {
          return {
            ...column,
            renderCell: (item: any) => (
              <Text
                style={{
                  fontSize: 14,
                  color: isDark ? colors.text.secondary : "rgb(97, 97, 97)",
                  textAlign: "center",
                  flexWrap: "wrap",
                  width: "100%",
                  padding: 4,
                }}
              >
                {formatPeriodWeek(item)}
              </Text>
            ),
          };
        }
        if (shouldCombine && column.columnKey === "Period") {
          combinedCreated = true;
          return {
            ...column,
            columnKey: "periodWeek",
            header: "Period Week",
            renderCell: (item: any) => (
              console.log("item", item),
              (
                <Text
                  style={{
                    fontSize: 14,
                    color: isDark ? colors.text.secondary : "rgb(97, 97, 97)",
                    textAlign: "center",
                    flexWrap: "wrap",
                    width: "100%",
                    padding: 4,
                  }}
                >
                  {formatPeriodWeek(item)}
                </Text>
              )
            ),
          };
        }
        if (shouldCombine && column.columnKey === "Week" && combinedCreated) {
          return null;
        }
        return column;
      })
      .filter((col: any) => col !== null);
  }, [tableColumns, styles]);

  const { width } = Dimensions.get("window");
  const isSmallScreen = width < 768;
  // Use isMobile prop for test compatibility, but also check platform for real usage
  // If isMobile is explicitly false, don't use card view; if not provided, use platform/width check
  const isIOSMobile = isMobile === false ? false : (Platform.OS === "ios" && width < 768);
  // Calculate dynamic minHeight based on number of records to avoid gaps with less data
  // Header: 48px, Row: 48px, Pagination: 60px, Buffer: 20px
  const calculateMinHeight = () => {
    const headerHeight = 48;
    const rowHeight = 48;
    const paginationHeight = 60;
    const bufferHeight = 20;
    const rowCount = tableData?.length || 0;
    const calculatedHeight = headerHeight + (rowCount * rowHeight) + paginationHeight + bufferHeight;
    const maxHeight = isSmallScreen ? 400 : 520;
    // Use calculated height if less than max, otherwise use max
    return Math.min(calculatedHeight, maxHeight);
  };
  const minTableHeight = calculateMinHeight();

  // If mobile filter overlay is visible, return only the overlay (modal behavior)
  if (isIOSMobile && isMobileFilterOverlayVisible) {
    return (
      <MobileFilterOverlay
        visible={isMobileFilterOverlayVisible}
        onDismiss={handleMobileFilterCancel}
        filters={filterOptions}
        onFilterChange={onFilterChange}
        onProceed={handleMobileFilterProceed}
        onCancel={handleMobileFilterCancel}
        onReset={resetFilters}
        currentFilters={currentFilters}
      />
    );
  }

  return (
    <View style={styles.cardOuterContainer ? styles.cardOuterContainer : localStyles.cardOuterContainer}>
      <View style={[styles.cardContainer ? styles.cardContainer : localStyles.cardContainer, isDark && { backgroundColor: colors.background.surface }]}>
        <View style={styles.metricsWrapper ? styles.metricsWrapper : localStyles.metricsWrapper}>
          {/* Filters */}

          {/* Header with Title and Mobile Filter Button */}
          <View style={localStyles.headerBox}>
            <Text
              style={[
                styles.tableHeading ? styles.tableHeading : localStyles.tableHeading,
                { fontSize: 24 },
                isDark && { color: colors.text.primary },
              ]}
            >
              Site Evaluation List
            </Text>

            <View style={localStyles.headerButtonsContainer}>
              {/* Mobile filter button - only show when prop is true */}
              {showMobileFilterButton && (
                <TouchableOpacity
                  style={localStyles.mobileFilterButton}
                  onPress={handleMobileFilterPress}
                >
                  <MaterialIcons name="filter-list" size={20} color="#FFFFFF" />
                  <Text style={localStyles.mobileFilterButtonText}>Filter</Text>
                </TouchableOpacity>
              )}
              {/* Go to Action Plan button - web/tablet only; mobile shown below */}
              {onGoToActionPlan && !isIOSMobile && (
                <TouchableOpacity
                  style={localStyles.actionPlanButton}
                  onPress={onGoToActionPlan}
                >
                  <Text style={localStyles.actionPlanButtonText}>Go to Action Plan</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Go to Action Plan button - iOS mobile only, below the header row */}
          {onGoToActionPlan && isIOSMobile && (
            <View style={localStyles.mobileActionPlanRow}>
              <TouchableOpacity
                style={localStyles.actionPlanButton}
                onPress={onGoToActionPlan}
              >
                <Text style={localStyles.actionPlanButtonText}>Go to Action Plan</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Filters Section - hide when mobile filter button is shown */}
          {!showMobileFilterButton && (
            <View style={styles.filterContainer ? styles.filterContainer : localStyles.filterContainer}>
              <LHFilter
                filters={filterOptions}
                onFilterChange={onFilterChange}
                itemsPerRow={isMobile ? 1 : 4}
                showActionButton={!!applyFilters}
                onActionButtonPress={applyFilters}
                actionButtonLabel="Apply Filters"
                onClearAllFilters={resetFilters}
              />
            </View>
          )}

          {/* Loading message */}
          {isLoading && (
            <View style={styles.messageContainer ? styles.messageContainer : localStyles.messageContainer}>
              <Text style={[styles.loadingText ? styles.loadingText : localStyles.loadingText, isDark && { color: colors.text.secondary }]}>
                Loading site evaluation Data...
              </Text>
            </View>
          )}

          {/* Table / Card View */}
          {isIOSMobile ? (
            // Mobile card view with pagination
            <View style={localStyles.mobileTableContainer}>
              <SiteEvalCardView
                data={tableData}
                columns={customizedColumns}
                onRowClick={onRowClick}
                onToggleChange={onToggleChange}
                isLoading={isLoading}
                activeItemsData={activeItemsData}
                isActiveItemsTable={false}
              />
            </View>
          ) : (
            // Web/Tablet table view
            <View
              style={
                styles.dataTableContainer
                  ? [styles.dataTableContainer, { minHeight: minTableHeight }, isDark && { backgroundColor: colors.background.surface }]
                  : [localStyles.dataTableContainer, { minHeight: minTableHeight }, isDark && { backgroundColor: colors.background.surface }]
              }
            >
              <SiteEvalDataTable
                data={tableData}
                columns={customizedColumns}
                tableHeader={""}
                tableDescription={""}
                onRowClick={onRowClick}
                activeItemsData={activeItemsData}
                isActiveItemsTable={false}
                onToggleChange={onToggleChange}
                columnFlexValues={{
                  CountOfActionPlans: 1.5,
                }}
              />
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const localStyles = StyleSheet.create({
  // Outer card container (shadow/background)
  cardOuterContainer: {
    // Outer wrapper that provides the elevation/box-shadow
    borderRadius: 8,
    // shadowColor: 'rgba(0,94,192,0.08)',
    // shadowOffset: { width: 0, height: 0 },
    // shadowOpacity: 1,
    // shadowRadius: 24,
    // elevation: 3,
    marginVertical: 12,
    width: '100%',
    alignSelf: 'center',
    // Mobile / iOS adjustments
    ...(Platform.OS === 'ios' && Dimensions.get('window').width < 768 && {
      marginVertical: 0,
      borderRadius: 0,
      shadowColor: 'transparent',
      elevation: 0,
      flex: 1,
    }),
  },
  // Inner white card that holds content
  cardContainer: {
    backgroundColor: Platform.OS === 'web' || (Platform.OS === 'ios' && Dimensions.get('window').width >= 768) ? '#FFF' : 'transparent',
    borderRadius: 8,
    overflow: Platform.OS === 'ios' ? 'visible' : 'hidden',
    width: '100%',
    alignSelf: 'center',
    // elevation: 4,
    // shadowColor: 'rgba(0,94,192,0.08)',
    // shadowOffset: { width: 0, height: 0 },
    // shadowOpacity: 1,
    // shadowRadius: 24,
    // Add shadow for web
    ...(Platform.OS === 'web' && {
     // boxShadow: '0 0 24px 0 rgba(0,94,192,0.08)',
    }),
    ...(Platform.OS === 'ios' && Dimensions.get('window').width < 768 && {
      borderRadius: 0,
      elevation: 0,
      shadowColor: 'transparent',
      backgroundColor: 'transparent',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    }),
  },

  metricsWrapper: {
    width: '100%',
    backgroundColor: 'transparent',
    // marginTop: 12,
    padding: 24,
    ...(Platform.OS === 'ios' && Dimensions.get('window').width < 768 && {
      padding: 0,
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    }),
  },
  filterContainer: {
   paddingHorizontal:8,
   paddingVertical: 20
  },
  // Table container mirrors ShiftHandOver / SparcWalk dataTableContainer
  dataTableContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    marginTop: '1%',
    padding: 0,
  },
  mobileTableContainer: {
    flex: 1,
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    marginTop: 0,
    paddingBottom: 120,
  },
  messageContainer: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  loadingText: {
    color: '#616161',
  },
  tableHeading: {
    fontFamily: FontFamily.bold,
    fontSize: 24,
    fontWeight: '700',
    color: '#2D2D2D',
    // marginBottom: 12,
    lineHeight: 32,
    padding:8
  },
  headerBox: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical:12,
    paddingHorizontal: 8,
  },
  headerButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionPlanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#034895',
    borderRadius: 24,
    paddingVertical: 8,
    paddingHorizontal: 16,
    height: 40,
  },
  actionPlanButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: FontFamily.bold,
  },
  mobileFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#034895',
    borderRadius: 24,
    paddingVertical: 4,
    paddingHorizontal: 16,
    height: 32,
    gap: 4,
  },
  mobileFilterButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: FontFamily.bold,
  },
  mobileActionPlanRow: {
    paddingHorizontal: 8,
    paddingBottom: 8,
    alignItems: 'flex-start',
  },
});

export default SiteEvalMetrics;
